import DragDropResizable from './DragDropResizable';
import DragDropDraggable from './DragDropDraggable';
import DragDropDroppable from './DragDropDroppable';
import DragDropResizableOptions from '../interfaces/DragDropResizableOptions';
import DragDropDroppableOptions from '../interfaces/DragDropDroppableOptions';
import DragDropElementHost from '../interfaces/DragDropElementHost';
import DragDropDraggableOptions from '../interfaces/DragDropDraggableOptions';

/**
 * DragDropElement class
 */
export default class DragDropElement {

  /**
   * Initialize a DragDropElement
   * @param el The element to make draggable, droppable, or resizable
   * @returns {DragDropElement}
   */
  static init(el: DragDropElementHost): DragDropElement {
    if (!el.ddElement) { 
      el.ddElement = new DragDropElement(el); 
    }
    return el.ddElement;
  }

  /**
   * The draggable object
   */
  public ddDraggable?: DragDropDraggable;

  /**
   * The droppable object
   */
  public ddDroppable?: DragDropDroppable;

  /**
   * The resizable object
   */
  public ddResizable?: DragDropResizable;

  /**
   * Constructor
   * @param el The element to make draggable, droppable, or resizable
   */
  constructor(public el: DragDropElementHost) {}

  /**
   * Add an event listener
   * @param eventName The event to listen to
   * @param callback The callback to call when the event is triggered
   * @returns {DragDropElement}
   */
  public on(eventName: string, callback: (event: MouseEvent) => void): DragDropElement {
    if (this.ddDraggable && ['drag', 'dragstart', 'dragstop'].indexOf(eventName) > -1) {
      this.ddDraggable.on(eventName as 'drag' | 'dragstart' | 'dragstop', callback);
    } else if (this.ddDroppable && ['drop', 'dropover', 'dropout'].indexOf(eventName) > -1) {
      this.ddDroppable.on(eventName as 'drop' | 'dropover' | 'dropout', callback);
    } else if (this.ddResizable && ['resizestart', 'resize', 'resizestop'].indexOf(eventName) > -1) {
      this.ddResizable.on(eventName as 'resizestart' | 'resize' | 'resizestop', callback);
    }
    return this;
  }

  /**
   * Remove an event listener
   * @param eventName The event to remove the listener from
   * @returns {DragDropElement}
   */
  public off(eventName: string): DragDropElement {
    if (this.ddDraggable && ['drag', 'dragstart', 'dragstop'].indexOf(eventName) > -1) {
      this.ddDraggable.off(eventName as 'drag' | 'dragstart' | 'dragstop');
    } else if (this.ddDroppable && ['drop', 'dropover', 'dropout'].indexOf(eventName) > -1) {
      this.ddDroppable.off(eventName as 'drop' | 'dropover' | 'dropout');
    } else if (this.ddResizable && ['resizestart', 'resize', 'resizestop'].indexOf(eventName) > -1) {
      this.ddResizable.off(eventName as 'resizestart' | 'resize' | 'resizestop');
    }
    return this;
  }

  /**
   * Setup Draggable
   * @param opts The options for the draggable
   * @returns {DragDropElement}
   */
  public setupDraggable(opts: DragDropDraggableOptions): DragDropElement {
    if (!this.ddDraggable) {
      this.ddDraggable = new DragDropDraggable(this.el, opts);
    } else {
      this.ddDraggable.updateOption(opts);
    }
    return this;
  }

  /**
   * Clean Draggable
   * @returns {DragDropElement}
   */
  public cleanDraggable(): DragDropElement {
    if (this.ddDraggable) {
      this.ddDraggable.destroy();
      delete this.ddDraggable;
    }
    return this;
  }

  /**
   * Setup Resizable
   * @param opts The options for the resizable
   * @returns {DragDropElement}
   */
  public setupResizable(opts: DragDropResizableOptions): DragDropElement {
    if (!this.ddResizable) {
      this.ddResizable = new DragDropResizable(this.el, opts);
    } else {
      this.ddResizable.updateOption(opts);
    }
    return this;
  }

  /**
   * Clean Resizable
   * @returns {DragDropElement}
   */
  public cleanResizable(): DragDropElement {
    if (this.ddResizable) {
      this.ddResizable.destroy();
      delete this.ddResizable;
    }
    return this;
  }

  /**
   * Setup Droppable
   * @param opts The options for the droppable
   * @returns {DragDropElement}
   */
  public setupDroppable(opts: DragDropDroppableOptions): DragDropElement {
    if (!this.ddDroppable) {
      this.ddDroppable = new DragDropDroppable(this.el, opts);
    } else {
      this.ddDroppable.updateOption(opts);
    }
    return this;
  }

  /**
   * Clean Droppable
   * @returns {DragDropElement}
   */
  public cleanDroppable(): DragDropElement {
    if (this.ddDroppable) {
      this.ddDroppable.destroy();
      delete this.ddDroppable;
    }
    return this;
  }
}
